import { FC, useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import PricingMenuItem, { PricingMenuItemProps } from 'components/atoms/PricingMenuItem';
import QueryParamLink from 'components/atoms/QueryParamLink';
import SeeMoreIconButton from '@z-components/atoms/SeeMoreIconButton';

import styles from './PricingMenuItemList.module.scss';

const NUMBER_OF_INITIAL_ITEMS_SHOWN = 3;

export type PricingMenuItem = Pick<
  PricingMenuItemProps,
  'description' | 'priceRangeEnd' | 'priceRangeStart'
> & { key: string };

interface PropsBase {
  canFilter?: boolean;
  isExpanded: boolean;
  items: Array<PricingMenuItem>;
  onShowMoreClick: VoidFunction;
  variant?: PricingMenuItemProps['variant'];
}

const PricingMenuItemListBase: FC<PropsBase> = ({
  canFilter,
  isExpanded,
  items,
  onShowMoreClick,
  variant,
}) => {
  const { query } = useRouter();
  const pricingMenuItemKeyQuery = useMemo(
    () => (typeof query.pricingMenuItemKey === 'string' ? query.pricingMenuItemKey : ''),
    [query.pricingMenuItemKey]
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <div>
      <ul className={styles.list}>
        {items.slice(0, isExpanded ? items.length : NUMBER_OF_INITIAL_ITEMS_SHOWN).map((item) => {
          const itemComponent = (
            <PricingMenuItem
              description={item.description}
              isArrowShown={canFilter}
              isHighlighted={item.key === pricingMenuItemKeyQuery}
              priceRangeStart={item.priceRangeStart}
              priceRangeEnd={item.priceRangeEnd}
              variant={variant}
            />
          );

          return (
            <li key={item.key}>
              {canFilter ? (
                <QueryParamLink
                  params={{
                    page: '',
                    pricingMenuItemKey: item.key === pricingMenuItemKeyQuery ? '' : item.key,
                  }}
                >
                  <a className={styles['pricing-menu-item-anchor']}>{itemComponent}</a>
                </QueryParamLink>
              ) : (
                itemComponent
              )}
            </li>
          );
        })}
      </ul>
      {!isExpanded && items.length > NUMBER_OF_INITIAL_ITEMS_SHOWN && (
        <div className={styles['see-more']}>
          <SeeMoreIconButton onClick={onShowMoreClick} />
        </div>
      )}
    </div>
  );
};

type Props = Omit<PropsBase, 'isExpanded' | 'onShowMoreClick'>;

const PricingMenuItemList: FC<Props> = ({ items, variant }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const handleShowMoreClick = useCallback(() => {
    setIsExpanded(true);
  }, []);

  return (
    <PricingMenuItemListBase
      isExpanded={isExpanded}
      items={items}
      onShowMoreClick={handleShowMoreClick}
      variant={variant}
    />
  );
};

export type { Props as PricingMenuItemListProps, PropsBase as PricingMenuItemListBaseProps };
export { PricingMenuItemListBase };
export default PricingMenuItemList;
